import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Header } from "../LandingPage1";
import { trackButtonClick } from "../utils/analytics";
import { sendToTelegram } from "../utils/telegramNotification";

// Partner tools configuration
const PARTNER_TOOLS = {
  fbtool: {
    name: "FBTool.net",
    tagline: "Đăng Bài & Marketing Tự Động Trên Facebook",
    description:
      "Công cụ tự động hoá các hoạt động trên Facebook: đăng bài hàng loạt lên Group & trang cá nhân, AI spin nội dung né spam, tìm kiếm & tương tác khách hàng 24/7.",
    platform: "Facebook",
    platformIcon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    color: "from-blue-500 to-indigo-600",
    accentColor: "blue",
    refLink: "https://fbtool.net?refcode=autowork",
    docLink: "https://doc.1man.io/fbtool.net",
    stats: [
      { value: "24/7", label: "Hoạt Động Liên Tục" },
      { value: "10x", label: "Tốc Độ Đăng Bài" },
      { value: "AI", label: "Spin Nội Dung Né Spam" },
      { value: "∞", label: "Số Group Không Giới Hạn" },
    ],
    features: [
      {
        title: "Đăng Bài Tự Động",
        description:
          "Tự động đăng bài lên hàng loạt Group và trang cá nhân Facebook. Hẹn giờ đăng, lặp lại theo lịch — hoạt động 24/7 không cần can thiệp.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        ),
        color: "from-blue-500 to-indigo-600",
      },
      {
        title: "AI Spin Nội Dung",
        description:
          "AI tự động viết lại nội dung cho mỗi bài đăng, né spam và trùng lặp. Mỗi Group nhận một phiên bản khác nhau — tự nhiên như viết tay.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        ),
        color: "from-amber-500 to-orange-600",
      },
      {
        title: "Tìm Kiếm Khách Hàng",
        description:
          "AI tự động quét comment trên bài quảng cáo, bài viral, hội nhóm — thu thập SĐT, email và thông tin liên hệ khách hàng tiềm năng.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        ),
        color: "from-emerald-500 to-green-600",
      },
      {
        title: "Tương Tác Tự Động",
        description:
          "Tự động like, comment, inbox khách hàng mới và cũ. Duy trì sự hiện diện liên tục trên Facebook — tăng reach và tương tác gấp nhiều lần.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        ),
        color: "from-pink-500 to-rose-600",
      },
    ],
    tags: ["Group Facebook", "Trang Cá Nhân", "AI Spin", "Tìm Khách Hàng"],
    seo: {
      title: "FBTool.net — Đăng Bài & Marketing Tự Động Facebook | AutoWork Partner",
      description: "FBTool.net — Công cụ đăng bài hàng loạt lên Group & trang cá nhân Facebook, AI spin nội dung né spam, tự động tìm kiếm và tương tác khách hàng 24/7.",
    },
  },
  zalotool: {
    name: "ZaloTool.net",
    tagline: "Kết Bạn & Chăm Sóc Khách Hàng Tự Động Trên Zalo",
    description:
      "Công cụ kết bạn hàng loạt, kéo nhóm, gửi tin nhắn cho 20,000 liên hệ chỉ 1 click, hiện thành viên ẩn — hỗ trợ tới 10 tài khoản Zalo trên một tài khoản tool.",
    platform: "Zalo",
    platformIcon: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.037 0 11.25c0 3.525 1.672 6.675 4.284 8.775-.117.675-.417 2.55-.483 2.944-.075.487.18.48.378.35.156-.103 2.484-1.687 3.51-2.381A13.425 13.425 0 0012 21.5c6.627 0 12-4.588 12-10.25S18.627 0 12 0zm5.79 14.869H14.3a.39.39 0 01-.39-.39V8.7a.39.39 0 01.78 0v5.39h3.1a.39.39 0 010 .78zM7.17 8.31a.39.39 0 01.39.39v5.78a.39.39 0 01-.78 0V8.7a.39.39 0 01.39-.39zm4.53 0a.39.39 0 01.33.18l2.73 4.2V8.7a.39.39 0 01.78 0v5.78a.39.39 0 01-.72.21l-2.73-4.2v3.99a.39.39 0 01-.78 0V8.7a.39.39 0 01.39-.39zM5.5 14.48a.39.39 0 010-.78h3.1V8.7a.39.39 0 01.78 0v5.39a.39.39 0 01-.39.39H5.5z" />
      </svg>
    ),
    color: "from-blue-400 to-cyan-500",
    accentColor: "cyan",
    refLink: "https://zalotool.net?refcode=autowork",
    docLink: "https://doc.1man.io/zalotool.net",
    stats: [
      { value: "20K", label: "Tin Nhắn / Click" },
      { value: "10", label: "Tài Khoản Zalo" },
      { value: "Auto", label: "Kết Bạn Tự Động" },
      { value: "24/7", label: "Hoạt Động Liên Tục" },
    ],
    features: [
      {
        title: "Kết Bạn Hàng Loạt",
        description:
          "Tự động kết bạn full tự động, tự chuyển sang tài khoản tiếp theo khi hoàn thành. Mở rộng mạng lưới khách hàng nhanh chóng.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        color: "from-blue-400 to-cyan-500",
      },
      {
        title: "Gửi Tin Nhắn Hàng Loạt",
        description:
          "Gửi tin nhắn cho 20,000 liên hệ chỉ với 1 click. Cá nhân hóa nội dung tin nhắn cho từng người nhận.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        ),
        color: "from-emerald-400 to-teal-500",
      },
      {
        title: "Kéo Nhóm & Hiện TV Ẩn",
        description:
          "Kéo thành viên vào nhóm Zalo tự động, hiện thành viên ẩn trong các nhóm — mở rộng danh sách khách hàng tiềm năng.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        ),
        color: "from-violet-400 to-purple-500",
      },
      {
        title: "Xoá Lời Mời Đã Gửi",
        description:
          "Tự động xoá lời mời kết bạn đã gửi, giữ tài khoản sạch sẽ và tránh bị giới hạn bởi Zalo.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        ),
        color: "from-rose-400 to-pink-500",
      },
    ],
    tags: ["Kết Bạn Hàng Loạt", "Tin Nhắn 20K", "10 Tài Khoản", "Kéo Nhóm"],
    seo: {
      title: "ZaloTool.net — Kết Bạn & Chăm Sóc Khách Hàng Zalo | AutoWork Partner",
      description: "ZaloTool.net — Công cụ kết bạn hàng loạt, gửi tin nhắn cho 20K liên hệ chỉ 1 click, kéo nhóm, hiện thành viên ẩn trên Zalo.",
    },
  },
  mailingtool: {
    name: "MailingTool.net",
    tagline: "Email Marketing Tự Động Bằng Gmail",
    description:
      "Công cụ gửi email hàng loạt bằng các tài khoản Gmail, tự động đổi tài khoản khi đạt giới hạn. AI spin và cá nhân hoá nội dung từng email, theo dõi người nhận đã mở, click.",
    platform: "Gmail",
    platformIcon: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
      </svg>
    ),
    color: "from-red-500 to-orange-500",
    accentColor: "red",
    refLink: "https://mailingtool.net?refcode=autowork",
    docLink: "https://doc.1man.io/mailingtool.net",
    stats: [
      { value: "AI", label: "Spin & Cá Nhân Hóa" },
      { value: "Auto", label: "Đổi Tài Khoản Gmail" },
      { value: "Track", label: "Theo Dõi Mở/Click" },
      { value: "∞", label: "Số Email Không Giới Hạn" },
    ],
    features: [
      {
        title: "Gửi Email Hàng Loạt",
        description:
          "Gửi email marketing hàng loạt bằng các tài khoản Gmail miễn phí. Tự động đổi tài khoản khi gửi đạt giới hạn.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        ),
        color: "from-red-500 to-orange-500",
      },
      {
        title: "AI Cá Nhân Hóa Nội Dung",
        description:
          "AI tự động spin và cá nhân hoá nội dung từng email, tạo cảm giác email được viết riêng cho mỗi người nhận.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        ),
        color: "from-amber-500 to-yellow-500",
      },
      {
        title: "Theo Dõi Mở & Click",
        description:
          "Theo dõi chính xác ai đã mở email, ai đã click vào link — giúp tối ưu chiến dịch email marketing hiệu quả.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        color: "from-emerald-500 to-teal-500",
      },
      {
        title: "Gửi Từ Gmail Của Bạn",
        description:
          "Email gửi trực tiếp từ tài khoản Gmail của bạn, người nhận có thể reply trực tiếp. Tăng tỷ lệ inbox và độ tin cậy.",
        icon: (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        ),
        color: "from-blue-500 to-indigo-500",
      },
    ],
    tags: ["Gmail Marketing", "AI Spin Email", "Tracking", "Auto Switch"],
    seo: {
      title: "MailingTool.net — Email Marketing Tự Động Bằng Gmail | AutoWork Partner",
      description: "MailingTool.net — Gửi email hàng loạt bằng Gmail, AI spin và cá nhân hoá nội dung, theo dõi người nhận đã mở và click.",
    },
  },
};

const TOOL_SLUGS = Object.keys(PARTNER_TOOLS);

const PartnerToolPage = () => {
  const { toolSlug } = useParams();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, [toolSlug]);

  const tool = PARTNER_TOOLS[toolSlug];

  if (!tool) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Không tìm thấy công cụ</h1>
          <p className="text-gray-400 mb-8">Công cụ bạn tìm kiếm không tồn tại.</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            Về Trang Chủ
          </button>
        </div>
      </div>
    );
  }

  const otherTools = TOOL_SLUGS.filter((s) => s !== toolSlug);

  return (
    <div className="bg-[#0F172A] min-h-screen">
      <Helmet>
        <title>{tool.seo.title}</title>
        <meta name="description" content={tool.seo.description} />
        <meta property="og:title" content={tool.seo.title} />
        <meta property="og:description" content={tool.seo.description} />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="relative pt-28 md:pt-36 pb-20 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 z-0">
          <div className={`absolute -top-[10%] -right-[10%] w-[55%] h-[55%] bg-gradient-to-br ${tool.color} opacity-20 rounded-full blur-[120px]`}></div>
          <div className="absolute bottom-[5%] -left-[10%] w-[45%] h-[45%] bg-gradient-to-br from-emerald-600/20 to-transparent rounded-full blur-[120px]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:60px_60px] opacity-40"></div>
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <a href="/" className="hover:text-white transition-colors">Trang chủ</a>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <a href="/#partner-tools" className="hover:text-white transition-colors">Đối Tác</a>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">{tool.name}</span>
          </nav>

          <div
            className={`max-w-4xl transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            {/* Platform Badge */}
            <div className="flex items-center gap-3 mb-6">
              <span className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${tool.color} bg-opacity-20 backdrop-blur-md text-white rounded-full text-sm font-bold shadow-lg border border-white/10`}>
                <span className="text-white">{tool.platformIcon}</span>
                <span>Đối Tác Chiến Lược — {tool.platform}</span>
              </span>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {tool.tags.map((tag, i) => (
                <span
                  key={i}
                  className="px-3 py-1 bg-white/5 text-gray-300 border border-white/10 rounded-full text-sm font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight mb-4">
              <span className="block mb-2">{tool.name}</span>
              <span className={`block text-transparent bg-clip-text bg-gradient-to-r ${tool.color}`}>
                {tool.tagline}
              </span>
            </h1>

            {/* Description */}
            <p className="text-gray-300 text-lg md:text-xl leading-relaxed max-w-3xl mb-10">
              {tool.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <a
                href={tool.refLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackButtonClick(`partner_${toolSlug}_cta`);
                  sendToTelegram(`Partner Tool Page - ${tool.name}`, 'Click "Trải nghiệm ngay"');
                }}
                className={`group relative inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r ${tool.color} text-white rounded-2xl font-bold text-lg shadow-2xl transform hover:-translate-y-1 hover:scale-105 transition-all duration-300`}
              >
                <span className="relative flex items-center gap-2">
                  <span>Trải Nghiệm {tool.name} Ngay</span>
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </span>
              </a>

            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 relative z-10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {tool.stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300 cursor-pointer"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(15px)",
                  transition: `all 0.5s ease ${300 + index * 100}ms`,
                }}
              >
                <div className={`text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${tool.color} mb-2`}>
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 relative z-10">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tính Năng Nổi Bật
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Khám phá các tính năng giúp bạn tự động hóa marketing trên {tool.platform}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {tool.features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-7 hover:bg-white/10 transition-all duration-500 overflow-hidden cursor-pointer"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0) scale(1)" : "translateY(30px) scale(0.95)",
                  transition: `all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 150}ms`,
                }}
              >
                {/* Glow */}
                <div className={`absolute -inset-0.5 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`}></div>
                
                <div className="relative z-10">
                  <div className={`relative mb-5 w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl"></div>
                    <div className="relative text-white">{feature.icon}</div>
                  </div>

                  <h3 className="text-xl lg:text-2xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-emerald-400 transition-all duration-300">
                    {feature.title}
                  </h3>

                  <p className="text-gray-400 text-sm lg:text-base leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Bottom */}
      <section className="py-16 relative z-10">
        <div className="container mx-auto px-6 md:px-12">
          <div className={`max-w-4xl mx-auto text-center p-10 md:p-16 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 relative overflow-hidden`}>
            <div className={`absolute -inset-1 bg-gradient-to-br ${tool.color} opacity-10 blur-2xl`}></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Sẵn sàng tự động hóa {tool.platform}?
              </h2>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                Bắt đầu sử dụng {tool.name} ngay hôm nay và trải nghiệm sức mạnh của tự động hóa marketing.
              </p>
              <a
                href={tool.refLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackButtonClick(`partner_${toolSlug}_bottom_cta`);
                  sendToTelegram(`Partner Tool Page - ${tool.name}`, 'Click "Bottom CTA"');
                }}
                className={`inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r ${tool.color} text-white rounded-2xl font-bold text-xl shadow-2xl transform hover:-translate-y-1 hover:scale-105 transition-all duration-300`}
              >
                Trải Nghiệm {tool.name} Ngay
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Other Tools */}
      <section className="py-16 relative z-10 border-t border-white/5">
        <div className="container mx-auto px-6 md:px-12">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">
            Khám Phá Các Công Cụ Khác
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {otherTools.map((slug) => {
              const t = PARTNER_TOOLS[slug];
              return (
                <a
                  key={slug}
                  href={`/partner/${slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/partner/${slug}`);
                    window.scrollTo(0, 0);
                  }}
                  className="group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 cursor-pointer"
                >
                  <div className={`absolute -inset-0.5 bg-gradient-to-br ${t.color} opacity-0 group-hover:opacity-15 blur-xl transition-opacity duration-500 rounded-2xl`}></div>
                  <div className="relative z-10 flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${t.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <div className="text-white">{t.platformIcon}</div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-emerald-400 transition-all duration-300">
                        {t.name}
                      </h4>
                      <p className="text-gray-400 text-sm">{t.platform} Marketing</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </a>
              );
            })}
          </div>

          {/* Back to Home */}
          <div className="text-center mt-10">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-300"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Quay lại Trang chủ AutoWork
            </a>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} AutoWork — Đối tác chiến lược của {tool.name}
          </p>
        </div>
      </footer>
    </div>
  );
};

export { PARTNER_TOOLS };
export default PartnerToolPage;
